<script>
import {Link} from "@inertiajs/vue3";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconCartPlus from "@js/components/Icons/IconCartPlus.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconDoubleAngleRight from "@js/components/Icons/IconDoubleAngleRight.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import QuantitySelector from "@js/components/Sections/Cart/QuantitySelector.vue";
import {useCartStore} from "@js/store/CartStore.js";
import {ref} from 'vue';
import {useCallbackStore} from "@js/store/CallbackStore.js";
import {Swiper, SwiperSlide} from "swiper/vue";
import "swiper/css";
import 'swiper/css/pagination'
import {Pagination} from 'swiper/modules'
import default_img from "@img/defaults/product-not-found.webp";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useCompareStore} from "@js/store/CompareStore.js";
import IconARBox from "@js/components/Icons/IconARBox.vue";
import {use3DViewStore} from "@js/store/3dViewStore.js";

export default {
    name: "CatalogProductItem",
    components: {
        IconARBox,
        IconScales,
        QuantitySelector,
        IconHeartFilled,
        ImageLoader,
        IconDoubleAngleRight,
        IconHeart,
        IconCartPlus,
        NumberFormatComponent,
        Link,
        Swiper,
        SwiperSlide,
    },
    props: {
        data: {
            required: true,
            type: Object,
        },
        animation_on: {
            required: false,
            type: Boolean,
            default: true,
        }
    },
    data() {
        return {
            swiper: null,
        }
    },
    methods: {
        setSwiper(swiperInstance) {
            this.swiper = swiperInstance;
        },
    },
    setup(props) {
        const likeStore = useLikeStore()
        const compareStore = useCompareStore()
        const cartStore = useCartStore()
        const callbackStore = useCallbackStore()
        const threeDViewStore = use3DViewStore()

        const qty = ref(cartStore.calculateArea(props.data.length, props.data.width));

        const productFromCart = cartStore.findItem(props.data.id)

        let unit = ref('m2');
        if (productFromCart) {
            unit = ref(productFromCart.unit);
        }

        function onQtyChange(value) {
            qty.value = value;
        }

        function onUnitChange(value) {
            unit.value = value;
        }

        return {
            likeStore,
            compareStore,
            cartStore,
            callbackStore,
            threeDViewStore,
            qty,
            unit,
            onQtyChange,
            onUnitChange,
            modules: [Pagination],
            default_img,
        }
    },
}
</script>

<template>
    <div class="relative">
        <div
            :class="{'md:transition-transform md:duration-300 md:ease-in-out transform md:hover:-translate-y-1 md:hover:scale-105 md:hover:shadow-lg': animation_on}"
            class="h-full flex flex-col bg-white shadow border border-gray-200 md:hover:border-baseColor max-md:active:border-baseColor rounded-xl">
            <div class="relative overflow-hidden rounded-t-xl flex flex-col z-1 shadow-sm bg-white">
                <div class="absolute left-0 z-2" v-if="data.interior_link"
                     @click="threeDViewStore.showPopup(data.interior_link)">
                    <div
                        class="bg-baseColor md:hover:bg-black text-black md:hover:text-white p-3 rounded-br-xl transition cursor-pointer">
                        <IconARBox class="w-8 h-8 transition"/>
                    </div>
                </div>
                <Link :href="route('product', {slug:data.slug})"
                      class="block w-full h-[175px]">
                    <div class="absolute right-0 z-2" v-if="data.bestseller">
                        <div class="font-bold text-[11px] text-white px-3 py-0.5 rounded-bl-md bg-[#c52314] leading-5">
                            ХИТ ПРОДАЖ
                        </div>
                    </div>
                    <Swiper v-if="data.image_urls && data.image_urls.length"
                            :slides-per-view="1"
                            :space-between="0"
                            @swiper="setSwiper"
                            :modules="modules"
                            :pagination="{ clickable: true }"
                            :loop="data.image_urls.length > 1"
                            :autoplay="{ delay: 5000, disableOnInteraction: true }"
                            class="pagination-base long_bullet bg-transparent h-full">
                        <SwiperSlide v-for="(image, key) in data.image_urls">
                            <ImageLoader :title="'Изображение продукта ' + key"
                                         :src="image"
                                         imgClasses="w-full h-full object-cover"/>
                        </SwiperSlide>
                    </Swiper>
                    <ImageLoader v-else
                                 title="Изображение не найдено" :src="default_img"
                                 imgClasses="w-full h-full object-cover"/>
                </Link>
            </div>
            <div class="p-4 flex-1 flex flex-col">
                <!-- Описание -->
                <Link :href="route('product', {slug:data.slug})"
                      class="font-medium text-base cursor-pointer mb-2 md:hover:text-navHoverColor transition">
                    {{ data.title }}
                </Link>
                <div class="flex flex-col gap-2 mb-5">
                    <div class="flex items-center gap-2 text-sm leading-tight">
                        <p v-if="data.brands_title">{{ data.brands_title }}</p>
                        <img v-if="data.brands_country_code"
                             :src="'/assets/flags/' + data.brands_country_code + '.svg'"
                             title="flag" alt="flag"
                             class="h-3 w-4 rounded object-cover"/>
                        <p v-if="data.countries_title">{{ data.countries_title }}</p>
                    </div>
                    <div class="text-nowrap text-sm leading-tight">
                        Артикул: {{ data.article ?? 'Н/Д' }}
                    </div>
                    <div class="text-nowrap text-sm leading-tight">
                        Размер: {{ data.sizes_title ?? 'Н/Д' }} см
                    </div>
                    <div class="text-nowrap text-sm leading-tight">
                        Цена:
                        <template v-if="data.price">
                            <NumberFormatComponent :number="data.price" class="text-[20px] font-semibold"/>
                            руб./м2
                        </template>
                        <span v-else class="text-[20px] font-semibold">
                            Договорная
                        </span>
                    </div>
                </div>
                <div class="flex flex-col mt-auto">
                    <div class="flex flex-col gap-2">
                        <QuantitySelector
                            v-if="data.price"
                            :key="data.id"
                            :product-id="data.id"
                            :model-value="cartStore.calculateArea(data.length, data.width)"
                            :model-unit="unit"
                            :area-per-piece="cartStore.calculateArea(data.length, data.width)"
                            :count-in-box="data.count_in_box"
                            @update:value="onQtyChange"
                            @update:unit="onUnitChange"/>
                        <div class="flex items-center justify-between gap-2">
                            <template v-if="data.price">
                                <button v-if="!cartStore.hasItem(data.id)"
                                        type="button" @click.prevent="cartStore.addToCart(data, unit, qty)"
                                        class="flex items-center justify-center w-full gap-2 leading-tight text-nowrap bg-baseColor md:hover:bg-baseColor/80 text-black focus:outline-none focus:ring-0 font-medium rounded text-sm px-5 py-2.5 transition">
                                    В корзину
                                    <IconCartPlus class="w-4 h-4"/>
                                </button>
                                <Link v-else type="button" :href="route('cart')"
                                      class="group flex items-center justify-center w-full gap-2 text-white leading-tight text-nowrap bg-emerald-600 md:hover:bg-emerald-600/80 focus:outline-none focus:ring-0 font-medium rounded text-sm px-5 py-2.5 transition">
                                <span class="group-hover:hidden">
                                    Добавлено в корзину
                                </span>
                                    <span class="hidden group-hover:block">
                                    К корзине
                                </span>
                                </Link>
                            </template>
                            <template v-else>
                                <button type="button" @click.prevent="callbackStore.showBaseCallbackPopup({
                                                title: 'Мы предложим лучшую цену',
                                                bitrix_title: 'Заявка на уточнение цены',
                                                comment: 'Продукт: ' + data.title,
                                                img_src: data.image_urls?.[0] ?? null,
                                            })"
                                        class="flex items-center justify-center w-full gap-2 leading-tight text-nowrap bg-baseColor md:hover:bg-baseColor/80 text-black focus:outline-none focus:ring-0 font-medium rounded text-sm px-5 py-2.5 transition">
                                    Узнать цену
                                </button>
                            </template>
                        </div>
                    </div>
                    <hr class="my-2"/>
                    <div class="flex items-center justify-between">
                        <div @click.prevent="likeStore.toggleLike(data.id)"
                             :class="{'text-red-600': likeStore.isLiked(data.id)}"
                             class="flex items-center gap-1 leading-tight text-nowrap font-medium text-xs cursor-pointer transition md:hover:text-likeColor">
                            <template v-if="!likeStore.isLiked(data.id)">
                                <IconHeart class="min-w-4 min-h-4 max-w-4 max-h-4 mb-[3px]"/>
                                В избранное
                            </template>
                            <template v-else>
                                <IconHeartFilled class="min-w-4 min-h-4 max-w-4 max-h-4 mb-[3px]"/>
                                В избранном
                            </template>
                        </div>
                        <div @click.prevent="compareStore.toggleCompare(data.id)"
                             :class="{'text-blue-500': compareStore.isCompared(data.id)}"
                             class="flex items-center gap-1 leading-tight text-nowrap font-medium text-xs cursor-pointer transition md:hover:text-blue-500">
                            <template v-if="!compareStore.isCompared(data.id)">
                                <IconScales class="min-w-4 min-h-4 max-w-4 max-h-4 mb-[3px]"/>
                                В сравнение
                            </template>
                            <template v-else>
                                <IconScales class="min-w-4 min-h-4 max-w-4 max-h-4 mb-[3px] text-blue-500"/>
                                Добавлено в сравнение
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.z-1 {
    z-index: 1;
}

.z-2 {
    z-index: 2;
}

.img-loading {
    max-width: 0 !important;
    max-height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    position: absolute !important;
}
</style>
